import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronUp, ArrowLeft, Save, X, User, Phone, AlertTriangle, Target, Settings, Calendar, Camera, CreditCard, CalendarDays, Repeat, BarChart3, ScrollText, Home } from 'lucide-react';
import ClientAccountingHistory from '../components/accounting/ClientAccountingHistory';
import BookingPlanner from '../components/planner/BookingPlanner';
import TrainingMetricsDashboard from '../components/dashboard/TrainingMetricsDashboard';
import ClientFlexibleRoutines from '../components/client/ClientFlexibleRoutines';
import EoaEAdminPanel from '../components/eoae/EoaEAdminPanel';
import { MuscleHeatmapCard } from '../components/workout/MuscleHeatmapCard';
import { useContinuity } from '../components/trainer/ContinuityChip';
import { CycleContextBlock } from '../components/cycles/CycleContextBlock';
import { CyclesTab } from '../components/cycles/CyclesTab';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Profile Avatar Component with upload capability
const ProfileAvatar = ({ imageUrl, name, clientId, onImageUpdate, canEdit = false }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/users/${clientId}/profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Refresh the client data
      if (onImageUpdate) onImageUpdate();
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
    <div className="relative">
      <div 
        className={`w-16 h-16 rounded-full overflow-hidden flex items-center justify-center ${
          imageUrl ? '' : 'bg-blue-500/30'
        } ${canEdit ? 'cursor-pointer' : ''}`}
        onClick={() => canEdit && fileInputRef.current?.click()}
        title={canEdit ? 'Click to change photo' : ''}
      >
        {imageUrl ? (
          <img 
            src={`${BACKEND_URL}${imageUrl}`} 
            alt={name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className={`w-full h-full flex items-center justify-center ${imageUrl ? 'hidden' : ''}`}
          style={{ display: imageUrl ? 'none' : 'flex' }}
        >
          {uploading ? (
            <div className="animate-spin w-6 h-6 border-2 border-blue-200 border-t-transparent rounded-full" />
          ) : (
            <span className="text-blue-200 text-xl font-bold">{initials}</span>
          )}
        </div>
      </div>
      
      {canEdit && (
        <>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-400 transition-colors"
            title="Change photo"
            disabled={uploading}
          >
            <Camera className="w-3 h-3 text-white" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, alertCount = 0 }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-blue-900/20 rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-blue-800/30 transition-colors"
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400" />}
          <span className="text-white font-medium">{title}</span>
          {alertCount > 0 && (
            <span className="bg-red-500/30 text-red-200 text-xs px-2 py-0.5 rounded-full">
              {alertCount} alert{alertCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-blue-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-blue-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 border-t border-blue-400/20">
          {children}
        </div>
      )}
    </div>
  );
};

// Field Display Component (for view mode)
const FieldDisplay = ({ label, value, className = '' }) => (
  <div className={className}>
    <label className="block text-blue-300 text-xs mb-1">{label}</label>
    <p className="text-white text-sm">{value || '-'}</p>
  </div>
);

// Field Input Component (for edit mode)
const FieldInput = ({ label, type = 'text', value, onChange, placeholder, options, rows, className = '', ...props }) => (
  <div className={className}>
    <label className="block text-blue-200 text-sm font-medium mb-1">{label}</label>
    {type === 'select' ? (
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="input-field text-sm w-full"
        {...props}
      >
        <option value="">Select...</option>
        {options?.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    ) : type === 'textarea' ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="input-field text-sm w-full"
        placeholder={placeholder}
        rows={rows || 3}
        {...props}
      />
    ) : type === 'checkbox' ? (
      <label className="flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={value || false}
          onChange={(e) => onChange(e.target.checked)}
          className="mr-2 w-4 h-4"
          {...props}
        />
        <span className="text-blue-200 text-sm">{placeholder}</span>
      </label>
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(type === 'number' ? (e.target.value ? parseFloat(e.target.value) : '') : e.target.value)}
        className="input-field text-sm w-full"
        placeholder={placeholder}
        {...props}
      />
    )}
  </div>
);

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  // Initialize activeTab from URL param or default to 'profile'
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'planner') return 'planner';
    if (tabParam === 'cycles') return 'cycles';
    if (tabParam === 'metrics') return 'metrics';
    if (tabParam === 'eoae') return 'eoae';
    return 'profile';
  });
  const cycleAutoAction = searchParams.get('action'); // e.g. 'close' from Hoy
  // Bumped whenever a cycle is created/edited/closed/deleted so dependent
  // surfaces (CycleContextBlock in the header) refresh without prop drilling.
  const [cycleVersion, setCycleVersion] = useState(0);
  const bumpCycleVersion = () => setCycleVersion((v) => v + 1);
  const [stats, setStats] = useState({
    completedTrainings: 0,
    lastTrainingDate: null,
    trainingFrequency30Days: 0
  });

  // Trainer-only operational signal — accumulated training exposure.
  // See services/training_continuity.py + /app/memory/P0_3_CONTINUITY_DESIGN.md.
  const { data: continuity } = useContinuity(clientId);

  // Check if we should start in edit mode (from URL param)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit') === 'true') {
      setIsEditMode(true);
    }
  }, []);

  // Calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        setLoading(true);
        // Fetch client details, workouts, and bookings in parallel
        const [clientsRes, workoutsRes, bookingsRes] = await Promise.all([
          axios.get(`${API}/users/clients`),
          axios.get(`${API}/workouts?client_id=${clientId}`),
          axios.get(`${API}/bookings?client_id=${clientId}&date_range=all&limit=1000`)
        ]);

        // Find the specific client
        const clientData = clientsRes.data.find(c => c.id === clientId);
        if (!clientData) {
          alert('Client not found');
          navigate('/dashboard');
          return;
        }

        setClient(clientData);
        setFormData(clientData);

        // Handle both old array format and new paginated format
        const bookingsData = Array.isArray(bookingsRes.data) 
          ? bookingsRes.data 
          : (bookingsRes.data.bookings || []);

        // Calculate stats
        const validBookingIds = new Set(bookingsData.map(b => b.id));
        const completedWorkouts = workoutsRes.data.filter(w => 
          w.is_finished === true && 
          w.booking_id && 
          validBookingIds.has(w.booking_id)
        );

        // Calculate training frequency (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentWorkouts = completedWorkouts.filter(w => {
          const workoutDate = new Date(w.finished_at || w.date);
          return workoutDate >= thirtyDaysAgo;
        });

        // Get last training date
        let lastDate = null;
        if (completedWorkouts.length > 0) {
          const sorted = completedWorkouts.sort((a, b) => {
            const dateA = new Date(a.finished_at || a.date);
            const dateB = new Date(b.finished_at || b.date);
            return dateB - dateA;
          });
          lastDate = sorted[0].finished_at || sorted[0].date;
        }

        setStats({
          completedTrainings: completedWorkouts.length,
          lastTrainingDate: lastDate,
          trainingFrequency30Days: recentWorkouts.length
        });

      } catch (error) {
        console.error('Error fetching client data:', error);
        alert('Error loading client data');
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchClientData();
    }
  }, [clientId, navigate]);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save changes
  const handleSave = async () => {
    try {
      setSaving(true);
      const submitData = {
        ...formData,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        member_number: formData.member_number ? parseInt(formData.member_number) : null,
        monthly_fee: formData.monthly_fee ? parseFloat(formData.monthly_fee) : null,
        created_at: formData.created_at ? (formData.created_at.includes('T') ? formData.created_at : `${formData.created_at}T00:00:00`) : null
      };

      await axios.put(`${API}/users/${clientId}`, submitData);
      setClient(formData);
      setIsEditMode(false);
      alert('Client updated successfully!');
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Error saving client: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Cancel edit
  const handleCancel = () => {
    setFormData(client);
    setIsEditMode(false);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Check for alerts (injuries or important notes)
  const alertCount = useMemo(() => {
    let count = 0;
    if (client?.injury_history_note) count++;
    return count;
  }, [client]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading client data...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Client not found</div>
      </div>
    );
  }

  const age = calculateAge(formData.birth_date || client.birth_date);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      {/* Header */}
      <nav className="glass-nav p-4">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center rounded-lg hover:bg-white/[0.04] transition-colors">
              <button
                onClick={() => {
                  // One-click default: return to Clients list
                  sessionStorage.setItem('activeTab', 'clients');
                  navigate('/dashboard');
                }}
                className="flex items-center gap-2 text-blue-200 hover:text-white transition-colors pl-1 pr-2 py-1"
                data-testid="back-to-clients-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Clients</span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center text-blue-300 hover:text-white p-1.5 rounded-lg transition-colors border-l border-white/10"
                    title="Choose where to go back"
                    data-testid="back-to-menu-btn"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-slate-900 border-blue-400/20 text-blue-200">
                  {[
                    { tab: 'hoy', label: '🏠 Today' },
                    { tab: 'calendar', label: '📅 Calendar' },
                    { tab: 'bookings', label: '📋 Bookings' },
                    { tab: 'clients', label: '👥 Clients' },
                  ].map((d) => (
                    <DropdownMenuItem
                      key={d.tab}
                      onClick={() => {
                        sessionStorage.setItem('activeTab', d.tab);
                        navigate('/dashboard');
                      }}
                      data-testid={`back-to-${d.tab}`}
                      className="cursor-pointer focus:bg-blue-500/20 focus:text-blue-100"
                    >
                      {d.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {isEditMode ? (
              <>
                <button
                  onClick={handleCancel}
                  className="btn-secondary flex items-center gap-2"
                  disabled={saving}
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary flex items-center gap-2"
                  disabled={saving}
                  data-testid="save-client-btn"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditMode(true)}
                className="btn-primary"
                data-testid="edit-client-btn"
              >
                Edit Client
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto p-6">
        {/* Client Header Card */}
        <div className="glass-card p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <ProfileAvatar 
                imageUrl={client.profile_image_url}
                name={client.full_name}
                clientId={clientId}
                onImageUpdate={() => {
                  // Refresh client data to show new image
                  axios.get(`${API}/users/clients`).then(res => {
                    const updatedClient = res.data.find(c => c.id === clientId);
                    if (updatedClient) {
                      setClient(updatedClient);
                      setFormData(updatedClient);
                    }
                  });
                }}
                canEdit={true}
              />
              <div>
                <h1 className="text-2xl font-bold text-white" data-testid="client-name">
                  {client.full_name}
                </h1>
                <p className="text-blue-300">{client.email}</p>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {/* ERP: Client Status Badge */}
                  {client.client_status === 'onboarding' ? (
                    <span className="bg-yellow-500/20 text-yellow-200 text-xs px-2 py-0.5 rounded">Onboarding</span>
                  ) : client.client_status === 'active' || client.is_active !== false ? (
                    <span className="bg-green-500/20 text-green-200 text-xs px-2 py-0.5 rounded">Active</span>
                  ) : (
                    <span className="bg-red-500/20 text-red-200 text-xs px-2 py-0.5 rounded">Inactive</span>
                  )}
                  {/* ERP: Booking Mode Badge */}
                  {client.booking_mode === 'monthly_fixed' && (
                    <span className="bg-purple-500/20 text-purple-200 text-xs px-2 py-0.5 rounded">Monthly Fixed</span>
                  )}
                  {client.booking_mode === 'booking_flex' && (
                    <span className="bg-blue-500/20 text-blue-200 text-xs px-2 py-0.5 rounded">Flex Booking</span>
                  )}
                  {client.member_number && (
                    <span className="text-blue-400 text-xs">Member #{client.member_number}</span>
                  )}
                  {client.created_at && (
                    <span className="text-blue-400 text-xs" data-testid="member-since">
                      · Member since {formatDate(client.created_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-300">{stats.completedTrainings}</div>
                <div className="text-blue-300 text-xs">Total Sessions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-300">{stats.trainingFrequency30Days}</div>
                <div className="text-blue-300 text-xs">Last 30 Days</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-200">{formatDate(stats.lastTrainingDate) || '-'}</div>
                <div className="text-blue-300 text-xs">Last Session</div>
              </div>
            </div>
          </div>
        </div>

        {/* Operational Cycle Context — calm, EMR-style coaching anchor.
            Replaces the inline continuity chip with a richer block that
            ties cycle + deload + continuity together in one place. */}
        <div className="mb-6">
          <CycleContextBlock clientId={clientId} continuity={continuity} refreshKey={cycleVersion} />
        </div>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-800/50 p-1 mb-6">
            <TabsTrigger 
              value="profile" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-blue-300 px-6 py-2"
              data-testid="tab-profile"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="planner" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-blue-300 px-6 py-2"
              data-testid="tab-planner"
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Planner
            </TabsTrigger>
            <TabsTrigger
              value="cycles"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-blue-300 px-6 py-2"
              data-testid="tab-cycles"
            >
              <Repeat className="w-4 h-4 mr-2" />
              Cycles
            </TabsTrigger>
            <TabsTrigger
              value="free_routines"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-amber-300 px-6 py-2"
              data-testid="tab-free-routines"
            >
              <Home className="w-4 h-4 mr-2" />
              Free Routines
            </TabsTrigger>
            <TabsTrigger
              value="metrics"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-blue-300 px-6 py-2"
              data-testid="tab-metrics"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Metrics
            </TabsTrigger>
            <TabsTrigger
              value="eoae"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-amber-300 px-6 py-2"
              data-testid="tab-eoae"
            >
              <ScrollText className="w-4 h-4 mr-2" />
              End of an Era
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab Content */}
          <TabsContent value="profile" className="mt-0">
            <div className="space-y-4">
              {/* Personal Information */}
              <CollapsibleSection title="Personal Information" icon={User} defaultOpen={true}>
                {isEditMode ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                    <FieldInput
                      label="Full Name"
                  value={formData.full_name}
                  onChange={(v) => handleFieldChange('full_name', v)}
                  required
                />
                <FieldInput
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(v) => handleFieldChange('email', v)}
                  required
                />
                <FieldInput
                  label="Member Number"
                  type="number"
                  value={formData.member_number}
                  onChange={(v) => handleFieldChange('member_number', v)}
                />
                <div>
                  <FieldInput
                    label="Birth Date"
                    type="date"
                    value={formData.birth_date}
                    onChange={(v) => handleFieldChange('birth_date', v)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  {age && <p className="text-blue-300 text-xs mt-1">Age: {age} years</p>}
                </div>
                <FieldInput
                  label="Gender"
                  type="select"
                  value={formData.gender}
                  onChange={(v) => handleFieldChange('gender', v)}
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
                  ]}
                />
                <FieldInput
                  label="Nationality"
                  value={formData.nationality}
                  onChange={(v) => handleFieldChange('nationality', v)}
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
                <FieldDisplay label="Full Name" value={client.full_name} />
                <FieldDisplay label="Email" value={client.email} />
                <FieldDisplay label="Member Number" value={client.member_number ? `#${client.member_number}` : null} />
                <FieldDisplay label="Birth Date" value={formatDate(client.birth_date)} />
                <FieldDisplay label="Age" value={age ? `${age} years` : null} />
                <FieldDisplay label="Gender" value={client.gender?.replace('_', ' ')} />
                <FieldDisplay label="Nationality" value={client.nationality} />
              </div>
            )}
          </CollapsibleSection>

          {/* Contact Information */}
          <CollapsibleSection title="Contact Information" icon={Phone} defaultOpen={true}>
            {isEditMode ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <FieldInput
                  label="Phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(v) => handleFieldChange('phone', v)}
                />
                <FieldInput
                  label="Address"
                  value={formData.address}
                  onChange={(v) => handleFieldChange('address', v)}
                />
                <FieldInput
                  label="City"
                  value={formData.city}
                  onChange={(v) => handleFieldChange('city', v)}
                />
                <FieldInput
                  label="Postal Code"
                  value={formData.postal_code}
                  onChange={(v) => handleFieldChange('postal_code', v)}
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                <FieldDisplay label="Phone" value={client.phone} />
                <FieldDisplay label="Address" value={client.address} />
                <FieldDisplay label="City" value={client.city} />
                <FieldDisplay label="Postal Code" value={client.postal_code} />
              </div>
            )}
          </CollapsibleSection>

          {/* Emergency Contact */}
          <CollapsibleSection title="Emergency Contact" icon={AlertTriangle} defaultOpen={false}>
            {isEditMode ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                <FieldInput
                  label="Contact Name"
                  value={formData.emergency_contact_name}
                  onChange={(v) => handleFieldChange('emergency_contact_name', v)}
                  placeholder="Emergency contact name"
                />
                <FieldInput
                  label="Relationship"
                  value={formData.emergency_contact_relationship}
                  onChange={(v) => handleFieldChange('emergency_contact_relationship', v)}
                  placeholder="e.g., Spouse, Parent"
                />
                <FieldInput
                  label="Phone"
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={(v) => handleFieldChange('emergency_contact_phone', v)}
                  placeholder="Phone number"
                />
                <FieldInput
                  label="Legacy Contact Info"
                  value={formData.emergency_contact}
                  onChange={(v) => handleFieldChange('emergency_contact', v)}
                  placeholder="Combined contact info"
                  className="md:col-span-3"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
                <FieldDisplay label="Contact Name" value={client.emergency_contact_name} />
                <FieldDisplay label="Relationship" value={client.emergency_contact_relationship} />
                <FieldDisplay label="Phone" value={client.emergency_contact_phone} />
                {client.emergency_contact && (
                  <FieldDisplay label="Additional Info" value={client.emergency_contact} className="md:col-span-3" />
                )}
              </div>
            )}
          </CollapsibleSection>

          {/* Physical Stats */}
          <CollapsibleSection title="Physical Stats" icon={Target} defaultOpen={false}>
            {isEditMode ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <FieldInput
                  label="Weight (kg)"
                  type="number"
                  value={formData.weight}
                  onChange={(v) => handleFieldChange('weight', v)}
                  step="0.1"
                  min="20"
                  max="300"
                  placeholder="75.5"
                />
                <FieldInput
                  label="Height (cm)"
                  type="number"
                  value={formData.height}
                  onChange={(v) => handleFieldChange('height', v)}
                  step="0.1"
                  min="100"
                  max="250"
                  placeholder="175"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                <FieldDisplay label="Weight" value={client.weight ? `${client.weight} kg` : null} />
                <FieldDisplay label="Height" value={client.height ? `${client.height} cm` : null} />
                {client.weight && client.height && (
                  <FieldDisplay 
                    label="BMI" 
                    value={(client.weight / Math.pow(client.height / 100, 2)).toFixed(1)} 
                  />
                )}
              </div>
            )}
          </CollapsibleSection>

          {/* Training Objectives & Medical */}
          <CollapsibleSection 
            title="Training & Medical" 
            icon={Target} 
            defaultOpen={true}
            alertCount={alertCount}
          >
            {isEditMode ? (
              <div className="space-y-4 pt-4">
                <FieldInput
                  label="Training Objectives"
                  type="textarea"
                  value={formData.training_objectives}
                  onChange={(v) => handleFieldChange('training_objectives', v)}
                  placeholder="Client's fitness goals..."
                  rows={3}
                />
                <FieldInput
                  label="Injury History / Medical Notes"
                  type="textarea"
                  value={formData.injury_history_note}
                  onChange={(v) => handleFieldChange('injury_history_note', v)}
                  placeholder="Any injuries or medical conditions to be aware of..."
                  rows={3}
                />
              </div>
            ) : (
              <div className="space-y-4 pt-4">
                <div>
                  <label className="block text-blue-300 text-xs mb-1">Training Objectives</label>
                  <p className="text-white text-sm whitespace-pre-wrap bg-blue-900/30 p-3 rounded-lg">
                    {client.training_objectives || 'No objectives specified'}
                  </p>
                </div>
                {client.injury_history_note && (
                  <div className="bg-red-900/20 border border-red-400/30 p-3 rounded-lg">
                    <label className="block text-red-300 text-xs mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Injury History / Medical Notes
                    </label>
                    <p className="text-red-100 text-sm whitespace-pre-wrap">
                      {client.injury_history_note}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CollapsibleSection>

          {/* Administrative */}
          <CollapsibleSection title="Administrative & Billing" icon={Settings} defaultOpen={false}>
            {isEditMode ? (
              <div className="space-y-6 pt-4">
                {/* ERP: Client Status & Mode */}
                <div>
                  <h4 className="text-blue-200 text-sm font-medium mb-3">Client Configuration</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FieldInput
                      label="Client Status"
                      type="select"
                      value={formData.client_status}
                      onChange={(v) => handleFieldChange('client_status', v)}
                      options={[
                        { value: 'onboarding', label: 'Onboarding (New Client)' },
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' }
                      ]}
                    />
                    <FieldInput
                      label="Booking Mode"
                      type="select"
                      value={formData.booking_mode}
                      onChange={(v) => handleFieldChange('booking_mode', v)}
                      options={[
                        { value: 'booking_flex', label: 'Flex Booking (Self-Service)' },
                        { value: 'monthly_fixed', label: 'Monthly Fixed (Trainer Assigns)' }
                      ]}
                    />
                    <FieldInput
                      label="Tracking Mode"
                      type="select"
                      value={formData.tracking_mode}
                      onChange={(v) => handleFieldChange('tracking_mode', v)}
                      options={[
                        { value: 'on_demand', label: 'On Demand' },
                        { value: 'per_session', label: 'Per Session' }
                      ]}
                    />
                  </div>
                </div>
                
                {/* ERP: Commercial / Accounting */}
                <div>
                  <h4 className="text-blue-200 text-sm font-medium mb-3">Commercial / Accounting</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FieldInput
                      label="Monthly Fee"
                      type="number"
                      value={formData.monthly_fee}
                      onChange={(v) => handleFieldChange('monthly_fee', v)}
                      placeholder="e.g., 150"
                      step="0.01"
                      min="0"
                    />
                    <FieldInput
                      label="Currency"
                      type="select"
                      value={formData.currency || 'EUR'}
                      onChange={(v) => handleFieldChange('currency', v)}
                      options={[
                        { value: 'EUR', label: '€ EUR' },
                        { value: 'USD', label: '$ USD' },
                        { value: 'GBP', label: '£ GBP' }
                      ]}
                    />
                    <FieldInput
                      label="Payment Preference"
                      type="select"
                      value={formData.payment_preference}
                      onChange={(v) => handleFieldChange('payment_preference', v)}
                      options={[
                        { value: 'cash', label: 'Cash' },
                        { value: 'card', label: 'Card' },
                        { value: 'transfer', label: 'Bank Transfer' },
                        { value: 'monthly', label: 'Monthly Invoice' }
                      ]}
                    />
                  </div>
                </div>

                {/* Fiscal / Invoicing */}
                <div>
                  <h4 className="text-blue-200 text-sm font-medium mb-3">Fiscal / Invoicing</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FieldInput
                      label="Fiscal Name / Company"
                      value={formData.fiscal_name}
                      onChange={(v) => handleFieldChange('fiscal_name', v)}
                      placeholder="e.g., Circutec S.L."
                    />
                    <FieldInput
                      label="NIF / CIF"
                      value={formData.nif}
                      onChange={(v) => handleFieldChange('nif', v)}
                      placeholder="e.g., B96145010"
                    />
                    <FieldInput
                      label="Fiscal Address"
                      value={formData.fiscal_address}
                      onChange={(v) => handleFieldChange('fiscal_address', v)}
                      placeholder="Street, city, postal code"
                    />
                  </div>
                </div>

                
                {/* Registration Details */}
                <div>
                  <h4 className="text-blue-200 text-sm font-medium mb-3">Registration Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FieldInput
                      label="Registration Date"
                      type="date"
                      value={formData.created_at?.split('T')[0]}
                      onChange={(v) => handleFieldChange('created_at', v)}
                    />
                    <FieldInput
                      label="Referral Source"
                      value={formData.referral_source}
                      onChange={(v) => handleFieldChange('referral_source', v)}
                      placeholder="How did they find us?"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6 pt-4">
                {/* ERP: Client Configuration Display */}
                <div>
                  <h4 className="text-blue-300 text-xs font-medium mb-3">Client Configuration</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <FieldDisplay 
                      label="Client Status" 
                      value={
                        client.client_status === 'onboarding' ? 'Onboarding' :
                        client.client_status === 'active' ? 'Active' :
                        client.client_status === 'inactive' ? 'Inactive' :
                        (client.is_active !== false ? 'Active' : 'Inactive')
                      } 
                    />
                    <FieldDisplay 
                      label="Booking Mode" 
                      value={
                        client.booking_mode === 'monthly_fixed' ? 'Monthly Fixed' :
                        client.booking_mode === 'booking_flex' ? 'Flex Booking' :
                        'Not Set'
                      } 
                    />
                    <FieldDisplay 
                      label="Tracking Mode" 
                      value={
                        client.tracking_mode === 'per_session' ? 'Per Session' :
                        'On Demand'
                      } 
                    />
                  </div>
                </div>
                
                {/* ERP: Commercial Display */}
                <div>
                  <h4 className="text-blue-300 text-xs font-medium mb-3">Commercial / Accounting</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <FieldDisplay 
                      label="Monthly Fee" 
                      value={client.monthly_fee ? `${client.monthly_fee} ${client.currency || 'EUR'}` : 'Not Set'} 
                    />
                    <FieldDisplay label="Currency" value={client.currency || 'EUR'} />
                    <FieldDisplay label="Payment Preference" value={client.payment_preference || 'Not Set'} />
                  </div>
                </div>
                
                {/* Registration Details Display */}
                <div>
                  <h4 className="text-blue-300 text-xs font-medium mb-3">Registration Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <FieldDisplay label="Registration Date" value={formatDate(client.created_at)} />
                    <FieldDisplay label="Referral Source" value={client.referral_source} />
                  </div>
                </div>
              </div>
            )}
          </CollapsibleSection>

          {/* Legal Compliance Section - Admin Editable (English) */}
          <CollapsibleSection title="Legal Information" icon={Settings} defaultOpen={false}>
            {isEditMode ? (
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldInput
                      label="Terms Accepted"
                      type="checkbox"
                      value={formData.terms_accepted}
                      onChange={(v) => handleFieldChange('terms_accepted', v)}
                      placeholder="Client has accepted terms and conditions"
                    />
                  </div>
                  <div>
                    <FieldInput
                      label="Privacy Policy Accepted"
                      type="checkbox"
                      value={formData.privacy_policy_accepted}
                      onChange={(v) => handleFieldChange('privacy_policy_accepted', v)}
                      placeholder="Client has accepted privacy policy"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FieldInput
                    label="Terms Acceptance Date"
                    type="date"
                    value={formData.terms_accepted_at?.split('T')[0] || ''}
                    onChange={(v) => handleFieldChange('terms_accepted_at', v ? `${v}T00:00:00Z` : null)}
                  />
                  <FieldInput
                    label="Privacy Acceptance Date"
                    type="date"
                    value={formData.privacy_policy_accepted_at?.split('T')[0] || ''}
                    onChange={(v) => handleFieldChange('privacy_policy_accepted_at', v ? `${v}T00:00:00Z` : null)}
                  />
                  <FieldInput
                    label="Legal Version"
                    value={formData.legal_version || ''}
                    onChange={(v) => handleFieldChange('legal_version', v)}
                    placeholder="e.g., v1.0"
                  />
                </div>
                <p className="text-blue-400 text-xs mt-2">
                  ℹ️ Admin can manually update legal acceptance for legacy clients
                </p>
              </div>
            ) : (
              <div className="pt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-blue-900/30 p-4 rounded-lg">
                    <div className="text-blue-300 text-xs mb-1">Terms Accepted</div>
                    <div className="text-white font-medium">
                      {client.terms_accepted ? (
                        <span className="text-green-300">Yes</span>
                      ) : (
                        <span className="text-red-300">No</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-blue-900/30 p-4 rounded-lg">
                    <div className="text-blue-300 text-xs mb-1">Terms Acceptance Date</div>
                    <div className="text-white font-medium">
                      {client.terms_accepted_at ? formatDate(client.terms_accepted_at) : '-'}
                    </div>
                  </div>
                  <div className="bg-blue-900/30 p-4 rounded-lg">
                    <div className="text-blue-300 text-xs mb-1">Privacy Policy Accepted</div>
                    <div className="text-white font-medium">
                      {client.privacy_policy_accepted ? (
                        <span className="text-green-300">Yes</span>
                      ) : (
                        <span className="text-red-300">No</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-blue-900/30 p-4 rounded-lg">
                    <div className="text-blue-300 text-xs mb-1">Privacy Acceptance Date</div>
                    <div className="text-white font-medium">
                      {client.privacy_policy_accepted_at ? formatDate(client.privacy_policy_accepted_at) : '-'}
                    </div>
                  </div>
                  <div className="bg-blue-900/30 p-4 rounded-lg">
                    <div className="text-blue-300 text-xs mb-1">Legal Version</div>
                    <div className="text-white font-medium">
                      {client.legal_version || '-'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CollapsibleSection>

          {/* Payment History Section - Admin Editable (English) */}
          <CollapsibleSection title="Payment History" icon={CreditCard} defaultOpen={false}>
            <div className="pt-4">
              <ClientAccountingHistory clientId={client.id} isEditMode={isEditMode} />
            </div>
          </CollapsibleSection>

          {/* Training History Summary */}
          <CollapsibleSection title="Training History" icon={Calendar} defaultOpen={false}>
            <div className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-green-900/30 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-green-300">{stats.completedTrainings}</div>
                  <div className="text-green-200 text-xs">Completed Sessions</div>
                </div>
                <div className="bg-blue-900/30 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-blue-300">{stats.trainingFrequency30Days}</div>
                  <div className="text-blue-200 text-xs">Sessions (Last 30 Days)</div>
                </div>
                <div className="bg-purple-900/30 p-4 rounded-lg text-center">
                  <div className="text-lg font-bold text-purple-300">{formatDate(stats.lastTrainingDate) || '-'}</div>
                  <div className="text-purple-200 text-xs">Last Session</div>
                </div>
                <div className="bg-yellow-900/30 p-4 rounded-lg text-center">
                  <div className="text-lg font-bold text-yellow-300">
                    {stats.trainingFrequency30Days > 0 
                      ? `${(30 / stats.trainingFrequency30Days).toFixed(1)} days`
                      : '-'
                    }
                  </div>
                  <div className="text-yellow-200 text-xs">Avg. Between Sessions</div>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('planner')}
                style={{ 
                  background: 'linear-gradient(135deg, #9333ea, #7c3aed)',
                  color: 'white',
                  boxShadow: '0 4px 15px rgba(147, 51, 234, 0.4)'
                }}
                className="w-full px-6 py-3 rounded-xl font-semibold transition-all hover:opacity-90"
                data-testid="view-planner-btn"
              >
                <CalendarDays className="w-4 h-4 inline mr-2" />
                Open Weekly Planner
              </button>
            </div>
          </CollapsibleSection>
            </div>
          </TabsContent>

          {/* Planner Tab Content */}
          <TabsContent value="planner" className="mt-0">
            <BookingPlanner 
              clientId={clientId} 
              clientName={client.full_name}
            />
          </TabsContent>

          {/* Cycles Tab Content — operational home of mesocycles */}
          <TabsContent value="cycles" className="mt-0">
            <CyclesTab clientId={clientId} onChange={bumpCycleVersion} autoAction={cycleAutoAction} />
          </TabsContent>

          {/* Free Routines — trainer-side management of assigned flexible routines */}
          <TabsContent value="free_routines" className="mt-0">
            <ClientFlexibleRoutines clientId={clientId} />
          </TabsContent>

          {/* Metrics Tab Content — trainer-side metrics + current-cycle snapshot */}
          <TabsContent value="metrics" className="mt-0">
            <div data-testid="client-metrics-tab">
              <MuscleHeatmapCard clientId={clientId} language="es" defaultDays={28} title="Músculos (4 semanas)" className="mb-6" />
              <TrainingMetricsDashboard
                userId={clientId}
                language="es"
                timeRangeOverride="30d"
                isAdminView={true}
              />
            </div>
          </TabsContent>

          {/* End of an Era — commemorative keepsake */}
          <TabsContent value="eoae" className="mt-0">
            <EoaEAdminPanel clientId={clientId} clientName={client.full_name} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClientDetailPage;
